/* eslint-disable max-lines */
import { Box, Button, Chip, Stack, Tooltip } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { compact, isEmpty, isNil, map, omit, values } from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import orderEnum from 'enums/order.enum';

import { casingFormat, formatStateFilterLabel } from 'common/utils';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { filterOrders } from 'store/thunks/workQueueThunks';
import { tagsChipName } from 'modules/WorkQueue/utils/tagMatchMode';
import FacilityFilterStatusChip from './FacilityFilterStatusChip';

const defaultOrderStatus = [orderEnum.ORDER_VERIFICATION, orderEnum.ORDER_RE_VERIFICATION];

const getDateRange = (dateRange) => {
  const dateArray = Array.isArray(dateRange) ? dateRange : [dateRange];
  const dates = map(dateArray, (date) => {
    const [year, month, day] = date.split('-').map(Number);
    return format(new Date(year, month - 1, day), 'MM/dd/yyyy');
  });

  return dates.length === 1 ? `${dates[0]}` : `${dates[0]} - ${dates[1]}`;
};

const formatTag = (tag) => casingFormat(tag?.replace(/_/g, ' '));

const OrderVerificationStatusBar = () => {
  const dispatch = useDispatch();
  const filter = useSelector(({ workQueue: { orderVerification } }) => orderVerification.filter);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const practices =
    filter?.partnerIds && filter?.practiceIds
      ? compact(
          filter.partnerIds.flatMap((partnerId) => {
            const partner = partners?.find((p) => p.partnerUuid === partnerId);
            return (
              partner?.partnerPractices?.filter((practice) =>
                filter.practiceIds.includes(practice.practiceId)
              ) || []
            );
          })
        )
      : [];

  const practiceName = practices?.map((practice) => casingFormat(practice.practiceName));

  const isPracticeCleared = filter?.practiceIds && !practiceName;

  useEffect(() => {
    if (isPracticeCleared) {
      dispatch(getPartnerPractices({ partnerIds: [filter.partnerId] }));
    }
  }, [dispatch, isPracticeCleared, filter]);

  const removeFilter = (filterName) => () => {
    dispatch(
      filterOrders({
        filter: {
          ...filter,
          [filterName]: undefined,
          ...(filterName === 'orderStatus' && {
            orderStatus: defaultOrderStatus,
          }),
          ...(filterName === 'partnerIds' && { practiceIds: undefined }),
          ...(filterName === 'createdAt' && { createdBetween: undefined }),
          ...(filterName === 'createdBetween' && { createdAt: undefined }),
          // POM-6330: a mode outliving its tags would widen the next selection (Urgent included).
          ...(filterName === 'tags' && { tagMatchMode: undefined }),
        },
      })
    );
  };

  const removeAllFilter = () =>
    dispatch(filterOrders({ filter: { orderStatus: defaultOrderStatus } }));

  return (
    <Stack
      direction='row'
      sx={{ maxWidth: '80rem' }}
      spacing={1}
      flexWrap='wrap'
      rowGap={1}
      alignItems='center'
    >
      <FacilityFilterStatusChip
        removeFilter={removeFilter}
        selectedFacilities={filter?.fulfillingPharmacyIds || []}
      />

      {filter.orderId && (
        <Chip
          label={`Order Id = ${filter.orderId}`}
          variant='contained'
          onDelete={removeFilter('orderId')}
        />
      )}

      {filter.shipByDate && (
        <Chip
          label={`Ship By Date = ${format(parseISO(filter.shipByDate), 'MM/dd/yyyy')}`}
          variant='contained'
          onDelete={removeFilter('shipByDate')}
        />
      )}

      {filter.partnerIds && (
        <Chip
          label={
            <Tooltip
              title={`Partner = ${filter.partnerIds
                .map((partnerId) => {
                  const partner = partners.find((p) => p.partnerUuid === partnerId);
                  return casingFormat(partner?.partnerName || '');
                })
                .join(', ')}`}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(
                  filter?.partnerIds?.map((partnerId) => {
                    const partner = partners?.find((p) => p?.partnerUuid === partnerId);
                    return casingFormat(partner?.partnerName || '');
                  }),
                  'Partner '
                )}
              </Box>
            </Tooltip>
          }
          key='partners'
          variant='contained'
          onDelete={removeFilter('partnerIds')}
        />
      )}

      {filter?.partnerIds && filter?.practiceIds && (
        <Chip
          label={
            <Tooltip title={practiceName?.join(',')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(practiceName, 'Practice')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('practiceIds')}
        />
      )}

      {filter.patientName && (
        <Chip
          label={`Patient Name = ${filter.patientName}`}
          variant='contained'
          onDelete={removeFilter('patientName')}
        />
      )}

      {filter.partnerAccount && (
        <Chip
          label={`Partner Account = ${filter.partnerAccount}`}
          variant='contained'
          onDelete={removeFilter('partnerAccount')}
        />
      )}

      {filter?.orderStatus?.length < 2 && (
        <Chip
          label={`Status= ${filter.orderStatus}`}
          variant='contained'
          onDelete={removeFilter('orderStatus')}
        />
      )}

      {filter?.states && (
        <Chip
          label={
            <Tooltip title={filter?.states?.join(',')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter?.states, 'States')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('states')}
        />
      )}

      {filter.tags && (
        <Chip
          key='tags'
          label={
            <Tooltip title={filter.tags.map(formatTag).join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.tags.map(formatTag), tagsChipName(filter))}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('tags')}
          size='small'
        />
      )}

      {filter.tagsNotIn && (
        <Chip
          key='tags-not-in'
          label={
            <Tooltip title={filter.tagsNotIn.map(formatTag).join(', ')}>
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatStateFilterLabel(filter.tagsNotIn.map(formatTag), 'Tags Not In')}
              </Box>
            </Tooltip>
          }
          variant='contained'
          onDelete={removeFilter('tagsNotIn')}
          size='small'
        />
      )}

      {!isNil(filter.isReshipment) && (
        <Chip
          label={`Reshipments = ${filter.isReshipment ? 'Yes' : 'No'}`}
          variant='contained'
          onDelete={removeFilter('isReshipment')}
        />
      )}

      {filter?.createdAt && (
        <Chip
          label={`Date = ${getDateRange(filter.createdAt)}`}
          variant='contained'
          onDelete={removeFilter('createdAt')}
        />
      )}

      {filter?.createdBetween && (
        <Chip
          label={`Date Range = ${getDateRange(filter.createdBetween)}`}
          variant='contained'
          onDelete={removeFilter('createdBetween')}
        />
      )}

      {!isEmpty(compact(values(omit(filter, ['orderStatus'])))) && (
        <Button onClick={removeAllFilter}>Remove All</Button>
      )}
    </Stack>
  );
};

export default OrderVerificationStatusBar;
